import { inject, Injectable, NgZone } from '@angular/core';
import { OpenLayersMap } from '../openlayers-map/openlayers-map';
import { CesiumManager } from '../openlayers-map/cesium-map/cesium-manager';
import { combineLatest, finalize, map, Observable, switchMap, take, tap } from 'rxjs';
import {
  LayerManagerModel, LayerTypesEnum, MapExportOptions, MapExportResult, MapStyleModel, MapViewDetailsModel,
  MapViewerOptionsModel, ToolConfigModel, ToolModel, VectorLayerModel,
} from '../models';
import { ToolManagerModel } from '../models/tool-manager.model';
import { Layer, Vector as VectorLayer } from 'ol/layer.js';
import { MapStyleHelper } from '../helpers/map-style.helper';
import { MapTooltipModel } from '../models/map-tooltip.model';
import { OpenLayersMapTooltip } from '../openlayers-map/open-layers-map-tooltip';
import { FeatureModelType } from '../models/feature-model.type';
import { FeatureHelper } from '../helpers/feature.helper';
import { BoundsModel, ErrorResponseModel, FeatureModel, FeatureModelAttributes } from '@tailormap-viewer/api';
import { MapSizeHelper } from '../helpers/map-size.helper';
import { MapUnitEnum } from '../models/map-unit.enum';
import { Source } from 'ol/source.js';
import { default as LayerRenderer } from 'ol/renderer/Layer.js';
import { HttpClient, HttpXsrfTokenExtractor } from '@angular/common/http';
import { ToolsStatusModel } from '../models/tools-status.model';
import { withLatestFrom } from 'rxjs/operators';

export type OlLayerFilter = (layer: Layer<Source, LayerRenderer<any>>) => boolean;

@Injectable({
  providedIn: 'root',
})
export class MapService {

  private readonly map: OpenLayersMap;
  private ngZone = inject(NgZone);
  private httpXsrfTokenExtractor = inject(HttpXsrfTokenExtractor);

  constructor() {
    this.map = new OpenLayersMap(this.ngZone, this.httpXsrfTokenExtractor);
  }

  public initMap(options: MapViewerOptionsModel, initialOptions?: { initialCenter?: [number, number]; initialZoom?: number }) {
    this.map.initMap(options, initialOptions);
  }

  public render(el: HTMLElement) {
    this.map.render(el);
  }

  public refreshLayer(layerId: string) {
    this.getLayerManager$().pipe(take(1))
      .subscribe(manager => {
        manager.refreshLayer(layerId);
      });
  }

  public getLayerManager$(): Observable<LayerManagerModel> {
    return this.map.getLayerManager$();
  }

  public getToolManager$(): Observable<ToolManagerModel> {
    return this.map.getToolManager$();
  }

  public createTool$<T extends ToolModel, C extends ToolConfigModel>(tool: C): Observable<{ tool: T; manager: ToolManagerModel }> {
    let toolManager: ToolManagerModel;
    let toolId: string;
    return this.getToolManager$()
      .pipe(
        tap(manager => toolManager = manager),
        finalize(() => {
          if (!!toolId && !!toolManager) {
            toolManager.removeTool(toolId);
          }
        }),
        map(manager => ({ tool: manager.addTool<T, C>(tool), manager })),
        tap(({ tool: createdTool }) => toolId = createdTool?.id || ''),
      );
  }

  public getToolStatusChanged$(): Observable<ToolsStatusModel> {
    return this.getToolManager$().pipe(switchMap(manager => manager.getToolStatusChanged$()));
  }

  public someToolsEnabled$(owners: string[]): Observable<boolean> {
    return this.getToolStatusChanged$()
      .pipe(
        map(({ enabledTools }) => {
          const ownerSet = new Set(owners);
          return enabledTools.some(t => ownerSet.has(t.owner));
        }),
      );
  }

  public enableTool<T = Record<string, unknown>>(toolId?: string, disableOtherTools?: boolean, enableArgs?: T, forceEnableIfActivated?: boolean) {
    this.executeToolManagerAction(manager => {
      manager.enableTool<T>(toolId, disableOtherTools, enableArgs, forceEnableIfActivated);
    });
  }

  public disableTool(toolId?: string, preventAutoEnableTools?: boolean) {
    this.executeToolManagerAction(manager => {
      manager.disableTool(toolId, preventAutoEnableTools);
    });
  }

  public setSwitchedTool(switched: boolean) {
    this.executeToolManagerAction(manager => {
      manager.setSwitchedTool(switched);
    });
  }

  public disableAllTools() {
    this.executeToolManagerAction(manager => {
      manager.disableAllTools();
    });
  }

  public enableAutoEnabledTools() {
    this.executeToolManagerAction(manager => {
      manager.enableAutoEnabledTools();
    });
  }

  public executeToolManagerAction(callback: (manager: ToolManagerModel) => void) {
    this.getToolManager$()
      .pipe(take(1))
      .subscribe(manager => callback(manager));
  }

  public createVectorLayer$<T extends FeatureModelAttributes = FeatureModelAttributes>(
    layer: VectorLayerModel,
    vectorLayerStyle?: MapStyleModel | ((feature: FeatureModel<T>) => MapStyleModel),
  ): Observable<VectorLayer | null> {
    let layerManager: LayerManagerModel;
    return this.getLayerManager$()
      .pipe(
        tap(manager => layerManager = manager),
        finalize(() => {
          if (layerManager) {
            layerManager.removeLayer(layer.id);
          }
        }),
        withLatestFrom(this.getProjectionCode$()),
        map(([ manager, projectionCode ]) => {
          const vectorLayer = manager.addLayer<VectorLayer>(layer);
          if (vectorLayer) {
            vectorLayer.setStyle(MapStyleHelper.getStyle(vectorLayerStyle, projectionCode));
          }
          return vectorLayer;
        }),
      );
  }

  public renderFeatures$<T extends FeatureModelAttributes = FeatureModelAttributes>(
    layerId: string,
    featureGeometry$: Observable<FeatureModelType<T> | Array<FeatureModelType<T>>>,
    vectorLayerStyle?: MapStyleModel | ((feature: FeatureModel<T>) => MapStyleModel),
    config?: {
      zoomToFeature?: boolean | (() => boolean);
      centerFeature?: boolean | (() => boolean);
      updateWhileAnimating?: boolean;
    },
  ): Observable<VectorLayer | null> {
    return combineLatest([
      this.createVectorLayer$({
        id: layerId,
        name: `${layerId} layer`,
        layerType: LayerTypesEnum.Vector,
        visible: true,
        updateWhileAnimating: config?.updateWhileAnimating,
      }, vectorLayerStyle),
      featureGeometry$,
    ])
      .pipe(
        tap(([ vectorLayer, featureGeometry ]) => {
          if (!vectorLayer) {
            return;
          }
          vectorLayer.getSource()?.getFeatures().forEach(feature => {
            vectorLayer.getSource()?.removeFeature(feature);
          });
          const featureModels = FeatureHelper.getFeatures(featureGeometry, vectorLayer.getSource()?.getProjection()?.getCode());
          featureModels.forEach(feature => {
            vectorLayer.getSource()?.addFeature(feature);
          });
          const shouldZoom = this.getBoolean(config?.zoomToFeature);
          const shouldCenter = this.getBoolean(config?.centerFeature);
          if (shouldZoom) {
            this.map.zoomToFeatures(featureModels);
          } else if (shouldCenter) {
            this.map.centerFeatures(featureModels);
          }
        }),
        map(([vectorLayer]) => vectorLayer),
      );
  }

  public zoomToFeatures(features: FeatureModelType | Array<FeatureModelType>) {
    this.map.getProjection$().pipe(take(1)).subscribe(projection => {
      const featureModels = FeatureHelper.getFeatures(features, projection.getCode());
      this.map.zoomToFeatures(featureModels);
    });
  }

  private getBoolean(bool?: boolean | (() => boolean)) {
    return typeof bool === 'boolean'
      ? bool
      : (typeof bool === 'undefined' ? false : bool());
  }

  public createTooltip$(): Observable<MapTooltipModel> {
    let tooltip: MapTooltipModel;
    return this.map.getMap$()
      .pipe(
        finalize(() => {
          if (tooltip) {
            tooltip.destroy();
          }
        }),
        map(olMap => {
          if (tooltip) {
            tooltip.destroy();
          }
          tooltip = new OpenLayersMapTooltip(olMap);
          return tooltip;
        }),
      );
  }

  public getPixelForCoordinates$(coordinates: [number, number]): Observable<[number, number] | null> {
    return this.map.getPixelForCoordinates$(coordinates);
  }

  public getMapViewDetails$(): Observable<MapViewDetailsModel> {
    return this.map.getMapViewDetails$();
  }

  public getProjectionCode$(): Observable<string> {
    return this.map.getProjection$().pipe(map(p => p.getCode()));
  }

  /** Gets the UOM of the map as defined in the projection.
   * Will return 'm' in case the map's projection has not defined UOM (such as EPSG:28992).
   *
   * @see ol.proj.Units
   */
  public getUnitsOfMeasure$(): Observable<MapUnitEnum> {
    return this.map.getProjection$().pipe(map(
        p => p.getUnits() === undefined ? MapUnitEnum.m : p.getUnits().toLowerCase() as MapUnitEnum,
      ),
    );
  }

  public getRoundedCoordinates$(coordinates: [number, number]) {
    return this.getUnitsOfMeasure$()
      .pipe(
        map(MapSizeHelper.getCoordinatePrecision),
        map(decimals => coordinates.map(coord => coord.toFixed(decimals))),
      );
  }

  public zoomIn() {
    this.map.zoomIn();
  }

  public zoomOut() {
    this.map.zoomOut();
  }

  public zoomToInitialExtent() {
    this.map.zoomToInitialExtent();
  }

  public setCenterAndZoom(center: number[], zoom: number) {
      this.map.setCenterAndZoom(center, zoom);
  }

  public zoomTo(geometry: string, projectionCode: string, maxZoom?: number) {
    this.getProjectionCode$()
      .pipe(take(1))
      .subscribe(mapProjection => {
        this.map.zoomToGeometry(FeatureHelper.fromWKT(geometry, projectionCode, mapProjection), maxZoom);
      });
  }

  public zoomToXY(center: [number, number], zoomLevel?: number, animationDuration = 1000, ignoreWhileAnimating = false) {
    this.map.zoomTo(center, zoomLevel, animationDuration, ignoreWhileAnimating);
  }

  public zoomToBounds(bounds: BoundsModel, maxZoom?: number) {
    this.map.zoomToExtent([ bounds.minx, bounds.miny, bounds.maxx, bounds.maxy ], maxZoom);
  }

  public zoomToScale(scale: number) {
    this.map.zoomToScale(scale);
  }

  /**
   * Export the current map to an image.
   */
  public exportMapImage$(options: MapExportOptions): Observable<MapExportResult> {
    return this.map.exportMapImage$(options);
  }

  public getFeatureInfoForLayers$(
    layerId: string,
    coordinates: [number, number],
    httpService: HttpClient,
  ): Observable<FeatureModel[] | ErrorResponseModel> {
    return this.map.getFeatureInfoForLayers$(layerId, coordinates, httpService);
  }

  public setPadding(padding: number[]) {
    this.map.setPadding(padding);
  }

  public getCesiumManager$(): Observable<CesiumManager> {
    return this.map.getCesiumManager$();
  }

  public make3D(){
    this.map.make3d();
  }

  public switch3D(){
    this.map.switch3d();
  }

  public set3dTerrainOpacity(opacity: number){
    this.map.set3dTerrainOpacity(opacity);
  }

  public get3dTerrainOpacity$(): Observable<number> {
    return this.map.get3dTerrainOpacity$();
  }

  public getIn3d$(): Observable<boolean> {
    return this.map.getIn3d$();
  }

  public hasUserInteractedWithMap$(): Observable<boolean> {
    return this.map.hasUserInteractedWithMap$();
  }

  public getPointerDrag$(): Observable<void> {
    return this.map.getPointerDrag$();
  }

  public allowSnapping(allow: boolean) {
    this.map.allowSnapping(allow);
  }

  public setSnappingLayerStyle<T extends FeatureModelAttributes = FeatureModelAttributes>(vectorLayerStyle: MapStyleModel | ((feature: FeatureModel<T>) => MapStyleModel)) {
    this.map.setSnappingLayerStyle(MapStyleHelper.getStyle(vectorLayerStyle));
  }

  public setSnappingTolerance(tolerance: number) {
    this.map.setSnappingTolerance(tolerance);
  }

  public setSnappingFeatures<T extends FeatureModelAttributes = FeatureModelAttributes>(features: Array<FeatureModelType<T>>) {
    this.map.renderSnappingFeatures(features);
  }

  public getMapTargetElement$(): Observable<HTMLElement | null> {
    return this.map.getMap$().pipe(
      map(olMap => olMap.getTargetElement()),
    );
  }

}
